import React, { useState, useEffect } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { DATE_DISPLAY_FORMAT, toApiDate } from "../../utils/dateUtils";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Grid,
  Chip,
  Avatar,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Search, ArrowBack, Download, FilterList } from "@mui/icons-material";

const AllStudentsAnalytics = () => {
  const navigate = useNavigate();
  const { isInInstituteContext, selectedInstitute } = useSelector(
    (state) => state.instituteContext
  );
  const { user } = useSelector((state) => state.auth);
  const userRole = user?.role?.slug || user?.role || "student";
  const userInstitute = user?.institute?.name || "ABC Engineering College"; // User's institute

  // Only superadmin (not in institute context) can see global data
  const canViewGlobalData = userRole === "superadmin" && !isInInstituteContext;

  const [searchQuery, setSearchQuery] = useState("");
  const [instituteFilter, setInstituteFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Dynamic data from API
  const [institutes, setInstitutes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Services
  import("../../services/instituteService").then((m) => m.default).catch(() => null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setIsLoading(true);
      try {
        const analyticsService = (await import("../../services/analyticsService")).default;
        const instituteService = (await import("../../services/instituteService")).default;
        const deptService = (await import("../../services/departmentService")).default;

        if (canViewGlobalData) {
          const instRes = await instituteService.getInstitutes({ page: 1, limit: 50 });
          if (mounted && instRes?.data?.data) setInstitutes(instRes.data.data);
        }

        const deptRes = await deptService.getAllDepartments({ page: 1, limit: 100 });
        if (mounted && deptRes?.data?.data) setDepartments(deptRes.data.data);

        const studentsRes = await analyticsService.getStudents({
          page: 1,
          limit: 1000,
          ...(instituteFilter && canViewGlobalData ? { instituteId: instituteFilter } : {}),
          ...(departmentFilter ? { departmentId: departmentFilter } : {}),
          ...(fromDate && toDate ? { from: fromDate, to: toDate } : {}),
        });
        if (mounted) {
          const payload = studentsRes?.data?.data || studentsRes?.data || [];
          setStudents(Array.isArray(payload) ? payload : []);
        }
      } catch (e) {
        console.warn("Failed to load analytics data:", e.message || e);
        if (mounted) setStudents([]);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    load();
    return () => (mounted = false);
  }, [canViewGlobalData, instituteFilter, departmentFilter, fromDate, toDate]);

  const columns = [
    {
      field: "name",
      headerName: "Student",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar sx={{ bgcolor: "primary.main", width: 36, height: 36 }}>
            {params.row.name.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {params.row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {[params.row.studentId, params.row.rollNumber || params.row.rollNo].filter(Boolean).join(" • ") || "—"}
            </Typography>
          </Box>
        </Box>
      ),
    },
    { field: "email", headerName: "Email", width: 180 },
    { field: "institute", headerName: "Institute", width: 200 },
    { field: "department", headerName: "Department", width: 150 },
    { field: "semester", headerName: "Semester", width: 100 },
    // Attendance column commented out until attendance API/feature ready
    // { field: "attendance", headerName: "Attendance", width: 100 },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === "active" ? "success" : "default"}
        />
      ),
    },
  ];

  const filteredStudents = students.filter((student) => {
    const matchesSearch =
      (student.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (student.email || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (student.studentId || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (student.rollNumber || student.rollNo || "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = !statusFilter || (student.status || "active") === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = [
    {
      label: "Total Students",
      value: filteredStudents.length,
      color: "primary.main",
    },
    {
      label: "Active",
      value: filteredStudents.filter((s) => s.status === "active").length,
      color: "success.main",
    },
    {
      label: "Inactive",
      value: filteredStudents.filter((s) => s.status === "inactive").length,
      color: "error.main",
    },
  ];

  // Get page title based on context
  const getPageTitle = () => {
    if (canViewGlobalData) return "All Students";
    if (isInInstituteContext) return `Students - ${selectedInstitute?.name}`;
    return `Students - ${userInstitute}`;
  };

  const getPageSubtitle = () => {
    if (canViewGlobalData)
      return "View and filter all students across institutes";
    if (isInInstituteContext)
      return `Viewing students of ${selectedInstitute?.name}`;
    return `Viewing students of your institute`;
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            {getPageTitle()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getPageSubtitle()}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Download />}>
          Export
        </Button>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((stat, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{ color: stat.color }}
                >
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <FilterList color="action" />
            <Typography variant="subtitle1" fontWeight={600}>
              Filters
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search students..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {canViewGlobalData && (
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Institute</InputLabel>
                  <Select
                    value={instituteFilter}
                    label="Institute"
                    onChange={(e) => setInstituteFilter(e.target.value)}
                  >
                    <MenuItem value="">All Institutes</MenuItem>
                    {institutes.map((inst) => (
                      <MenuItem key={inst.id} value={String(inst.id)}>
                        {inst.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Department</InputLabel>
                <Select
                  value={departmentFilter}
                  label="Department"
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                >
                  <MenuItem value="">All Departments</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={String(dept.id)}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="From"
                format={DATE_DISPLAY_FORMAT}
                value={fromDate ? new Date(fromDate) : null}
                onChange={(val) => setFromDate(toApiDate(val))}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="To"
                format={DATE_DISPLAY_FORMAT}
                value={toDate ? new Date(toDate) : null}
                onChange={(val) => setToDate(toApiDate(val))}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setFromDate("");
                  setToDate("");
                }}
                disabled={!fromDate && !toDate}
                sx={{ height: 40 }}
              >
                Clear Date Range
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Data Grid */}
      <Card>
        <CardContent>
          <DataGrid
            rows={filteredStudents}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            sx={{ border: "none" }}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default AllStudentsAnalytics;
